import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Chip, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { ArrowBack, Download } from "@mui/icons-material";
import { toast } from "react-toastify";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import {
  isCreateAllowed,
  canConvertPRToPO,
  canViewPRs,
  isProcurementUser,
  prHasRestrictedCategory,
} from "../../../utils/common-utils";
import { checkIsApprover } from "../../../utils/utils";
import { getOfflineData } from "../../../utils/offline-services";
import CustomModal from "../../../../common-utilities/core/modal";
import MuiTable from "../../../../common-utilities/core/mui-table";
import ReconsiderAction from "../../reconsider-action";
import ResolvedStoresViewer from "../create-pr/resolved-stores-viewer";
import ApprovalInfo from "./approval-info";
import BulkPOModal from "./bulk-po-modal";
import EditPRConfirmDialog from "./edit-pr-confirm-dialog";
import PRApprovalCycles from "./pr-approval-cycles";
import EditedPRChanges from "./edited-pr-changes";
import "./index.scss";

const STATUS_CHIP_PROPS = {
  created: { label: "Draft", sx: { bgcolor: '#f5f5f5', color: '#757575' } },
  draft: { label: "Draft (Edited)", sx: { bgcolor: '#f5f5f5', color: '#757575' } },
  Draft: { label: "Draft (Edited)", sx: { bgcolor: '#f5f5f5', color: '#757575' } },
  approval_in_progress: { label: "Approval in Progress", sx: { bgcolor: '#f5f5f5', color: '#ed6c02' } },
  approved: { label: "Approved", sx: { bgcolor: '#f5f5f5', color: '#2e7d32' } },
  rejected: { label: "Rejected", sx: { bgcolor: '#f5f5f5', color: '#d32f2f' } },
  reconsider: { label: "Reconsider", sx: { bgcolor: '#f5f5f5', color: '#ed6c02' } },
  cancelled: { label: "Cancelled", sx: { bgcolor: '#f5f5f5', color: '#757575' } },
  converted: { label: "Converted to PO", sx: { bgcolor: '#f5f5f5', color: '#0288d1' } },
  short_close: { label: "Short Closed", sx: { bgcolor: '#f5f5f5', color: '#0288d1' } },
};

const PO_STATUS_CHIP_PROPS = {
  draft: { label: "Draft", sx: { bgcolor: '#f5f5f5', color: '#757575' } },
  approval_in_progress: { label: "Approval In Progress", sx: { bgcolor: '#f5f5f5', color: '#ed6c02' } },
  approved: { label: "Approved", sx: { bgcolor: '#f5f5f5', color: '#2e7d32' } },
  rejected: { label: "Rejected", sx: { bgcolor: '#f5f5f5', color: '#d32f2f' } },
  reconsider: { label: "Reconsider", sx: { bgcolor: '#f5f5f5', color: '#ed6c02' } },
  cancelled: { label: "Cancelled", sx: { bgcolor: '#f5f5f5', color: '#757575' } },
  short_close: { label: "Short Close", sx: { bgcolor: '#f5f5f5', color: '#0288d1' } },
};

const NOT_CREATED_CHIP = { label: "Not Created", sx: { bgcolor: '#f5f5f5', color: '#94A3B8' } };

const INLINE_STORE_CHIP_THRESHOLD = 3;

// Mirrors the backend's own special-role check for reconsidering an already-approved item
// (see reconsiderApproval in approval-service/index.js and reconsiderApprovalById in
// approval.controller.js) -- Approved-PR reconsideration is scoped to Department User only,
// not to normal approvers/creators. Both space and underscore forms are checked since this
// role's stored value has been seen with either elsewhere in this codebase
// (role-enitity-visibility.service.js, reports.controller.js) -- case normalization alone
// doesn't bridge that difference.
const RECONSIDER_APPROVED_PR_ROLES = ["DEPARTMENT USER", "DEPARTMENT_USER"];

const formatCurrency = (val) =>
  val != null
    ? `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "-";

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("en-IN") : "-";

// A HO PR fanned out to more than one store — the breakdown table only makes sense here.
const isMultiStoreHO = (pr) =>
  pr?.prScope === "HEAD_OFFICE" && (pr?.selectedSiteDetails?.length || 0) > 1;

// The Qty/Converted/Balance columns for a HEAD_OFFICE PR (SELF or multi-store) are always
// computed from item.allocations[] sums, never from the pooled quantity/convertedQuantity/
// remainingQuantity mirror fields -- those mirrors are only refreshed by an actual allocation
// movement (see allocation.service.js's recomputeConversionStatus) and can be stale/null
// immediately after PR creation, before any PO exists against it.
const isHeadOfficePR = (pr) => pr?.prScope === "HEAD_OFFICE";

// Same normalization as the backend's refId() (subway-finance-v2-api/src/utils/refId.js) --
// a Location-referencing field may be a plain id or a populated { id | _id, ... } object.
const normalizeId = (value) =>
  value && typeof value === "object" ? value.id ?? value._id : value;

// Mirrors the backend's allocation.service.js computeAvailable.
const computeAvailable = (allocation) =>
  (allocation?.requestedQuantity || 0) -
  (allocation?.reservedQuantity || 0) -
  (allocation?.convertedQuantity || 0) -
  (allocation?.shortClosedQuantity || 0);

const sumAllocations = (item, field) =>
  (item?.allocations || []).reduce((sum, allocation) => sum + (allocation[field] || 0), 0);

const sumAllocationsAvailable = (item) =>
  (item?.allocations || []).reduce((sum, allocation) => sum + computeAvailable(allocation), 0);

// Store label for a Short Close row -- selectedSiteDetails only covers HEAD_OFFICE PRs
// fanned out to more than one store; a SINGLE_STORE (or HO/SELF) PR's one allocation is
// against pr.site itself, which never appears in selectedSiteDetails.
const getStoreLabel = (pr, storeId) => {
  const match = (pr.selectedSiteDetails || []).find((s) => String(s.id) === String(storeId));
  if (match) return match.code ? `${match.code} - ${match.name}` : match.name || `Store ${storeId}`;
  if (String(normalizeId(pr.site)) === String(storeId)) {
    return pr.siteCode ? `${pr.siteCode} - ${pr.siteName}` : pr.siteName || `Store ${storeId}`;
  }
  return `Store ${storeId}`;
};

// Same itemCode/itemName fallback shape used elsewhere in this file for item rows.
const getItemIdentity = (row) => {
  const code = row.itemCode || row.item?.itemCode || row.item?.code || null;
  const name = row.itemName || row.item?.name || null;
  return { name, code };
};

const groupBy = (arr, keyFn) => {
  const map = new Map();
  arr.forEach((item) => {
    const key = keyFn(item);
    const bucket = map.get(key) || [];
    bucket.push(item);
    map.set(key, bucket);
  });
  return map;
};

// `pr` and `onShortCloseItem` are only needed to build the Short Close column, pushed on
// conditionally below -- pr.canShortClose already carries the creator-only/PO-issued
// gating computed server-side (see short-close.service.js's canShortClosePR), so this
// column only ever renders for the PR creator, and only once a partial PO exists.
const getPRItemsConfig = (isHeadOffice, pr, onShortCloseItem) => {
  const columns = [
    {
      label: "Item",
      id: "itemName",
      render: (row) =>
        row.itemName || row.item?.name || row.item?.itemCode || "-",
    },
    { label: "Description", id: "description", render: (row) => row.description || "-" },
    { label: "HSN", id: "hsnCode", render: (row) => row.hsnCode || "-" },
    { label: "UOM", id: "uom", render: (row) => row.uom || "-" },
    {
      label: isHeadOffice ? "Total PR Qty" : "PR Qty",
      id: "quantity",
      render: (row) => (isHeadOffice ? sumAllocations(row, "requestedQuantity") : row.quantity ?? "-"),
    },
    {
      label: isHeadOffice ? "Total PO Created Qty" : "Moved to PO",
      id: "convertedQuantity",
      render: (row) => (isHeadOffice ? sumAllocations(row, "convertedQuantity") : row.convertedQuantity ?? "-"),
    },
    // Moved to PO + Short Closed Qty + Balance Qty should always sum to PR Qty (modulo any
    // quantity transiently sitting in reservedQuantity between reserve and convert) --
    // all three are pooled mirrors kept in sync together by recomputeConversionStatus
    // (allocation.service.js).
    {
      label: isHeadOffice ? "Total Short Closed Qty" : "Short Closed Qty",
      id: "shortClosedQuantity",
      render: (row) => (isHeadOffice ? sumAllocations(row, "shortClosedQuantity") : row.shortClosedQuantity ?? "-"),
    },
    {
      label: isHeadOffice ? "Total Balance Qty" : "Balance Qty",
      id: "remainingQuantity",
      render: (row) => (isHeadOffice ? sumAllocationsAvailable(row) : row.remainingQuantity ?? "-"),
    },
    {
      label: "Rate (₹)",
      id: "estimatedRate",
      render: (row) => formatCurrency(row.estimatedRate),
    },
    {
      label: "Amount",
      id: "estimatedAmount",
      render: (row) => formatCurrency(row.estimatedAmount),
    },
    {
      label: "Tax %",
      id: "taxRate",
      render: (row) => (row.taxRate != null ? `${row.taxRate}%` : "-"),
    },
    {
      label: "Tax Amt",
      id: "estimatedTaxAmount",
      render: (row) => formatCurrency(row.estimatedTaxAmount),
    },
    {
      label: "Total",
      id: "estimatedTotalAmount",
      render: (row) => formatCurrency(row.estimatedTotalAmount),
    },
    { label: "Remarks", id: "remarks", render: (row) => row.remarks || "-" },
  ];

  if (pr?.canShortClose && isCreateAllowed("pr")) {
    columns.push({
      label: "Short Close",
      id: "shortCloseAction",
      render: (row) => {
        const available = sumAllocationsAvailable(row);
        if (available <= 0) {
          const alreadyShortClosed = sumAllocations(row, "shortClosedQuantity") > 0;
          return alreadyShortClosed ? (
            <Chip label="Short Closed" size="small" sx={{ bgcolor: '#f5f5f5', color: '#757575' }} />
          ) : (
            "-"
          );
        }
        return (
          <Button variant="text" size="small" onClick={() => onShortCloseItem(row)}>
            Short Close
          </Button>
        );
      },
    });
  }

  return columns;
};

// Same endpoint/blob handling as getPOasPdf in purchase-order/po-details/index.js,
// parameterized by poId since this table lists many POs rather than one page's own PO.
const downloadPoPdf = (poId) => {
  if (!poId) {
    toast.error("Unable to download: PO id is missing");
    return;
  }
  invokeApi(
    HTTP_METHODS.POST,
    `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_PO_PDF}${poId}`,
    null,
    null,
    null,
    "blob"
  )
    .then((response) => {
      if (response.code) {
        toast.error(response.message || "Failed to download PO");
      } else {
        const _url = window.URL.createObjectURL(response);
        window.open(_url, "_blank");
      }
    })
    .catch((err) => {});
};

const getRelatedPOColumns = (navigate, isMultiStore, stores = []) => {
  const columns = [
    { label: "PO Number", id: "poNumber", render: (po) => po.poNumber || "-" },
    {
      label: "Vendor",
      id: "vendorName",
      render: (po) =>
        po.vendorCode ? `${po.vendorCode} - ${po.vendorName || ""}` : po.vendorName || "-",
    },
  ];

  if (isMultiStore) {
    columns.push({
      label: "Store",
      id: "store",
      // Exact id match against the PO's own deliveryLocationId -- no more fuzzy
      // code/name string comparison.
      render: (po) => {
        const store = stores.find(
          (s) => String(s.id) === String(normalizeId(po.deliveryLocationId))
        );
        if (!store) return "-";
        return store.code ? `${store.code} - ${store.name || ""}` : store.name || "-";
      },
    });
  }

  columns.push(
    {
      label: "Status",
      id: "status",
      render: (po) => {
        const chipProps = PO_STATUS_CHIP_PROPS[po.status] || {
          label: po.status,
          sx: { bgcolor: '#f5f5f5', color: '#757575' },
        };
        return <Chip label={chipProps.label} sx={chipProps.sx} size="small" />;
      },
    },
    {
      label: "Quantity",
      id: "quantity",
      render: (po) => {
        const items = po.items || [];
        const total = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        if (items.length <= 1) return total;
        return (
          <Tooltip
            title={
              <>
                {items.map((item, idx) => (
                  <div key={idx}>
                    {item.itemName || item.item?.name || item.item?.itemCode || "-"} :{" "}
                    {item.quantity ?? 0}
                  </div>
                ))}
              </>
            }
          >
            <span>{total}</span>
          </Tooltip>
        );
      },
    },
    {
      label: "Actions",
      id: "view",
      render: (po) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/finance-v2/purchase-order/${po.id}`)}
          >
            View
          </Button>
          <IconButton
            size="small"
            title="Download PO"
            disabled={!po.id}
            onClick={() => downloadPoPdf(po.id)}
          >
            <Download fontSize="small" />
          </IconButton>
        </div>
      ),
    }
  );

  return columns;
};

const CHILD_SUBMITTABLE_STATUSES = ["created", "draft", "Draft"];
const getChildPRColumns = (navigate, onSubmitChild, submittingChildId) => [
  {
    label: "PR Number",
    id: "prNumber",
    render: (row) => (
      <Button
        variant="text"
        size="small"
        sx={{ textTransform: "none", fontWeight: 600 }}
        onClick={() => navigate(`/finance-v2/purchase-requisition/${row.id}`)}
      >
        {row.prNumber || row.id}
      </Button>
    ),
  },
  { label: "State", id: "gstState", render: (row) => row.gstState || "-" },
  {
    label: "Total Amount",
    id: "totalEstimatedGrossAmount",
    render: (row) => formatCurrency(row.totalEstimatedGrossAmount),
  },
  {
    label: "Status",
    id: "status",
    render: (row) => {
      const chipProps = STATUS_CHIP_PROPS[row.status] || {
        label: row.status,
        sx: { bgcolor: '#f5f5f5', color: '#757575' },
      };
      return <Chip label={chipProps.label} sx={chipProps.sx} size="small" />;
    },
  },
  {
    label: "Actions",
    id: "actions",
    render: (row) => (
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(`/finance-v2/purchase-requisition/${row.id}`)}
        >
          View
        </Button>
        {CHILD_SUBMITTABLE_STATUSES.includes(row.status) && (
          <Button
            variant="outlined"
            size="small"
            disabled={submittingChildId === row.id}
            onClick={() => onSubmitChild(row.id)}
          >
            Submit
          </Button>
        )}
      </div>
    ),
  },
];

// A bulk item upload across multiple stores can leave a store with no real allocation for an
// item (the actual quantity landed on a different store's row for the same item) -- that store
// still gets a zero-valued allocation entry rather than none at all, so buildAllocationRows below
// would otherwise render an empty-looking row with every figure at 0. Display-only: this never
// touches pr.items/allocations, it just decides whether an already-built row is worth showing.
const isAllZeroAllocationRow = (row) =>
  !row.requestedQty &&
  !row.reservedQty &&
  !row.convertedQty &&
  !row.shortClosedQty &&
  !row.availableQty &&
  !row.amount &&
  !row.taxAmount &&
  !row.totalAmount;

// Store x item breakdown, built directly from each item's persisted allocations[] --
// no rebuilding from item.quantity/selectedSites and no fuzzy PO matching. Linked POs are
// grouped by their own deliveryLocationId (an exact persisted id, populated by the backend's
// PurchaseRequest.purchaseOrderIds select), so there is nothing ambiguous left to flag.
const buildAllocationRows = (pr) => {
  const stores = pr.selectedSiteDetails || [];
  const items = pr.items || [];
  const posByStore = groupBy(pr.purchaseOrderIds || [], (po) => String(normalizeId(po.deliveryLocationId)));

  const rows = [];
  stores.forEach((store) => {
    const storePOs = posByStore.get(String(store.id)) || [];
    items.forEach((item, idx) => {
      const prItemId = item._id ?? item.id;
      const linkedPOs = storePOs
        .filter(
          (po) =>
            prItemId != null &&
            (po.items || []).some((poItem) => poItem?.prItemId != null && String(poItem.prItemId) === String(prItemId))
        )
        .map((po) => ({
          id: po.id,
          poNumber: po.poNumber,
          status: po.status,
        }));
      const allocation = (item.allocations || []).find(
        (a) => String(normalizeId(a.storeId)) === String(store.id)
      );
      const requestedQty = allocation?.requestedQuantity ?? 0;
      const reservedQty = allocation?.reservedQuantity ?? 0;
      const convertedQty = allocation?.convertedQuantity ?? 0;
      const shortClosedQty = allocation?.shortClosedQuantity ?? 0;
      const availableQty = allocation ? computeAvailable(allocation) : 0;
      const rate = item.estimatedRate || 0;
      const taxRate = item.taxRate || 0;
      const amount = requestedQty * rate;
      const taxAmount = amount * (taxRate / 100);
      rows.push({
        key: `${store.id}-${idx}`,
        storeLabel: store.code ? `${store.code} - ${store.name || ""}` : store.name || "-",
        itemLabel: getItemIdentity(item).name || getItemIdentity(item).code || "-",
        requestedQty,
        reservedQty,
        convertedQty,
        shortClosedQty,
        availableQty,
        amount,
        taxRate,
        taxAmount,
        totalAmount: amount + taxAmount,
        linkedPOs,
      });
    });
  });

  return { rows: rows.filter((row) => !isAllZeroAllocationRow(row)) };
};

// Named exports (component stays the default export) purely so the all-zero-row filter this
// function applies has direct unit test coverage -- see index.test.js.
export { buildAllocationRows, isAllZeroAllocationRow };

const getStoreAllocationColumns = (navigate) => [
  { label: "Store", id: "storeLabel", render: (row) => row.storeLabel },
  { label: "Item", id: "itemLabel", render: (row) => row.itemLabel },
  { label: "Requested Qty", id: "requestedQty", render: (row) => row.requestedQty ?? "-" },
  { label: "Reserved Qty", id: "reservedQty", render: (row) => row.reservedQty ?? "-" },
  { label: "Converted Qty", id: "convertedQty", render: (row) => row.convertedQty ?? "-" },
  { label: "Short-Closed Qty", id: "shortClosedQty", render: (row) => row.shortClosedQty ?? "-" },
  { label: "Available Qty", id: "availableQty", render: (row) => row.availableQty ?? "-" },
  { label: "Amount", id: "amount", render: (row) => formatCurrency(row.amount) },
  { label: "Tax Amt", id: "taxAmount", render: (row) => formatCurrency(row.taxAmount) },
  { label: "Total", id: "totalAmount", render: (row) => formatCurrency(row.totalAmount) },
  {
    label: "Linked PO",
    id: "linkedPOs",
    render: (row) =>
      row.linkedPOs.length === 0 ? (
        "-"
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          {row.linkedPOs.map((po) => (
            <Button
              key={po.id}
              size="small"
              variant="text"
              onClick={() => navigate(`/finance-v2/purchase-order/${po.id}`)}
            >
              {po.poNumber}
            </Button>
          ))}
        </div>
      ),
  },
  {
    label: "PO Status",
    id: "poStatus",
    render: (row) =>
      row.linkedPOs.length === 0 ? (
        <Chip label={NOT_CREATED_CHIP.label} sx={NOT_CREATED_CHIP.sx} size="small" />
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {[...new Set(row.linkedPOs.map((po) => po.status))].map((status) => {
            const chipProps = PO_STATUS_CHIP_PROPS[status] || {
              label: status,
              sx: NOT_CREATED_CHIP.sx,
            };
            return <Chip key={status} label={chipProps.label} sx={chipProps.sx} size="small" />;
          })}
        </div>
      ),
  },
];

const PRDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const approvalId = searchParams.get("approvalId");
  const [pr, setPR] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [approvalDetails, setApprovalDetails] = useState({});
  const [approveRejectDetails, setApproveRejectionDetails] = useState(null);
  const [sentPrDetails, setSentPrDetails] = useState(null);
  const [filters, setFilters] = useState({ page: 1 });
  const [allocationFilters, setAllocationFilters] = useState({ page: 1 });
  const [storesViewerAnchor, setStoresViewerAnchor] = useState(null);
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [bulkPOModalOpen, setBulkPOModalOpen] = useState(false);
  const [shortClosePRConfirmOpen, setShortClosePRConfirmOpen] = useState(false);
  const [rejectStorePRConfirmOpen, setRejectStorePRConfirmOpen] = useState(false);
  const [reconsiderStorePRConfirmOpen, setReconsiderStorePRConfirmOpen] = useState(false);
  const [shortCloseItem, setShortCloseItem] = useState(null);
  const [shortCloseRemarks, setShortCloseRemarks] = useState("");
  const [childPRs, setChildPRs] = useState([]);
  const [childPRsLoading, setChildPRsLoading] = useState(false);
  const [submittingChildId, setSubmittingChildId] = useState(null);
  const [submittingAllChildren, setSubmittingAllChildren] = useState(false);

  useEffect(() => {
    if (!canViewPRs()) {
      setIsLoading(false);
      return;
    }
    if (id) loadPR();
  }, [id]);

  useEffect(() => {
    if (pr?.isParentPR) loadChildPRs();
  }, [pr?.isParentPR, pr?.id]);

  useEffect(() => {
    approvalId && canViewPRs() && loadApprovalDetails();
  }, [approvalId]);

  useEffect(() => {
    console.log('pr.approvalId', pr?.approvalId);
    if (
      (pr?.approvalId && !approvalId) ||
      (pr?.approvalId && approvalId !== String(pr.approvalId))
    ) {
      setSearchParams({ approvalId: String(pr.approvalId) });
    }
  }, [pr?.approvalId]);

  const loadPR = () => {
    setIsLoading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}/${id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          setPR(res);
        }
      })
      .catch(() => toast.error("Failed to load PR"))
      .finally(() => setIsLoading(false));
  };

  const loadApprovalDetails = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_APPROVALS}`,
      null,
      { _id: approvalId, archive: false }
    )
      .then((response) => {
        if (response.length > 0) {
          setApprovalDetails(response[0]);
        }
      })
      .catch(() => {});
  };

  const onApprove = () => {
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.APPROVE}/${approvalDetails.id}`,
      null,
      { comment: approveRejectDetails.comment || "" }
    )
      .then((response) => {
        if (response.code) {
          toast.error(response.message || "Failed to approve");
        } else {
          loadPR();
          loadApprovalDetails();
          setApproveRejectionDetails(null);
          navigate(-1);
        }
      })
      .catch(() => {});
  };

  const onReject = () => {
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.REJECT}`,
      null,
      { comment: approveRejectDetails.comment || "", id: approvalDetails.id }
    )
      .then((response) => {
        if (response.code) {
          toast.error(response.message || "Failed to reject");
        } else {
          setApproveRejectionDetails(null);
          loadPR();
          loadApprovalDetails();
          navigate(-1);
        }
      })
      .catch(() => {});
  };

  const submitPR = () => {
    setIsActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SUBMIT}${id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          setSentPrDetails(res);
          loadPR();
        }
      })
      .catch(() => toast.error("Failed to submit PR"))
      .finally(() => setIsActionLoading(false));
  };

  const loadChildPRs = () => {
    setChildPRsLoading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}`,
      null,
      { parentPRId: id, financialYear: pr?.financialYear, limit: 100 }
    )
      .then((res) => {
        setChildPRs(res?.results || []);
      })
      .catch(() => toast.error("Failed to load linked state PRs"))
      .finally(() => setChildPRsLoading(false));
  };

  const submitOneChild = (childId) => {
    setSubmittingChildId(childId);
    invokeApi(HTTP_METHODS.POST, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SUBMIT}${childId}`)
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("State PR submitted for approval");
          loadChildPRs();
          loadPR();
        }
      })
      .catch(() => toast.error("Failed to submit state PR"))
      .finally(() => setSubmittingChildId(null));
  };

  const submitAllChildren = () => {
    setSubmittingAllChildren(true);
    invokeApi(HTTP_METHODS.POST, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SUBMIT_CHILDREN}${id}`)
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
          return;
        }
        const { succeededCount = 0, failedCount = 0, total = 0 } = res.summary || {};
        if (failedCount === 0) {
          toast.success(`Submitted for approval across ${succeededCount} state(s)`);
        } else if (succeededCount > 0) {
          toast.error(`${succeededCount} of ${total} state PRs submitted; ${failedCount} failed`);
        } else {
          toast.error("Failed to submit any state PR");
        }
        loadChildPRs();
        loadPR();
      })
      .catch(() => toast.error("Failed to submit state PRs"))
      .finally(() => setSubmittingAllChildren(false));
  };

  const cancelPR = () => {
    if (!window.confirm("Are you sure you want to cancel this PR?")) return;
    setIsActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_CANCEL}${id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("PR cancelled successfully");
          loadPR();
        }
      })
      .catch(() => toast.error("Failed to cancel PR"))
      .finally(() => setIsActionLoading(false));
  };

  const rejectStorePR = () => {
    setRejectStorePRConfirmOpen(false);
    setIsActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_REJECT_STORE}${id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("Store PR rejected successfully");
          loadPR();
        }
      })
      .catch(() => toast.error("Failed to reject Store PR"))
      .finally(() => setIsActionLoading(false));
  };

  const reconsiderStorePR = () => {
    setReconsiderStorePRConfirmOpen(false);
    setIsActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_RECONSIDER_STORE}${id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("Store PR sent back for reconsideration");
          loadPR();
        }
      })
      .catch(() => toast.error("Failed to reconsider Store PR"))
      .finally(() => setIsActionLoading(false));
  };

  // Standalone whole-PR short close (po-pr-short skill item #1b) -- independent of PO short
  // close, gated server-side by pr.canShortCloseWholePR (creator-only, Approved/Converted
  // status, and blocked while any linked PO is still open -- see short-close.service.js's
  // canShortCloseWholePR/shortCloseEntirePR). Confirmation is the in-app shortClosePRConfirmOpen
  // modal below (matching this screen's own CustomModal pattern), not a native browser confirm.
  const shortClosePRWhole = () => {
    setShortClosePRConfirmOpen(false);
    setIsActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SHORT_CLOSE_WHOLE}${id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("Purchase Requisition short closed successfully");
          loadPR();
        }
      })
      .catch(() => toast.error("Failed to short close PR"))
      .finally(() => setIsActionLoading(false));
  };

  // Opens the Short Close modal for this item -- all-or-nothing (po-pr-short skill item 0,
  // Reversal 1): there is no quantity to enter, closing an item closes 100% of its current
  // remaining balance across every store it's allocated to, in one call.
  const openShortCloseModal = (row) => {
    setShortCloseItem(row);
    setShortCloseRemarks("");
  };

  // Read-only summary of what will be closed -- not editable, unlike the old per-store
  // quantity inputs this replaced.
  const shortCloseOpenAllocations = shortCloseItem
    ? (shortCloseItem.allocations || []).filter((a) => computeAvailable(a) > 0)
    : [];

  const submitShortClose = () => {
    if (!shortCloseOpenAllocations.length) {
      toast.error("This item has no remaining quantity to short close");
      return;
    }

    setIsActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SHORT_CLOSE}${id}`,
      { items: [{ prItemId: shortCloseItem._id, remarks: shortCloseRemarks || undefined }] }
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("Item(s) short closed successfully");
          setShortCloseItem(null);
          loadPR();
        }
      })
      .catch(() => toast.error("Failed to short close item"))
      .finally(() => setIsActionLoading(false));
  };

  const convertToPO = () => {
    const budgetId = pr.budget?.id || pr.budget;
    navigate(`/finance-v2/purchase-order/create/${budgetId}?prId=${pr.id || pr._id}`);
  };

  // Same endpoint/blob call as getPOasPdf in purchase-order/po-details/index.js — opens the
  // PDF in a new tab rather than forcing a save, matching "Print PO"'s behavior.
  const printPR = () => {
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_PR_PDF}${id}`,
      null,
      null,
      null,
      "blob"
    )
      .then((response) => {
        if (response.code) {
          toast.error(response.message || "Failed to print PR");
        } else {
          const _url = window.URL.createObjectURL(response);
          window.open(_url, "_blank");
        }
      })
      .catch((err) => {});
  };

  // Same endpoint/blob call as printPR, but forces a file save using the same anchor-download
  // trick as grn-declaration-modal.js instead of opening the PDF in a new tab.
  const downloadPR = () => {
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_PR_PDF}${id}`,
      null,
      null,
      null,
      "blob"
    )
      .then((response) => {
        if (response.code) {
          toast.error(response.message || "Failed to download PR");
        } else {
          const _url = window.URL.createObjectURL(response);
          const alink = document.createElement("a");
          alink.href = _url;
          alink.download = `PR-${pr.prNumber || id}.pdf`;
          alink.click();
        }
      })
      .catch((err) => {});
  };

  if (isLoading) {
    return (
      <div className="finance-v2-po-details-container">
        <Typography color="text.secondary" sx={{ p: 2 }}>
          Loading PR details...
        </Typography>
      </div>
    );
  }

  if (!pr) {
    return (
      <div className="finance-v2-po-details-container">
        <Typography color="error" sx={{ p: 2 }}>
          PR not found.
        </Typography>
        <Button onClick={() => navigate(-1)} sx={{ ml: 2 }}>
          Go Back
        </Button>
      </div>
    );
  }
  const chipProps = STATUS_CHIP_PROPS[pr.status] || { label: pr.status, sx: { bgcolor: '#f5f5f5', color: '#757575' } };
  const items = pr.items || [];

  const headOfficePR = isHeadOfficePR(pr);
  const multiStoreHO = isMultiStoreHO(pr);
  const storeCount = pr.selectedSiteDetails?.length || 0;
  // All four sums come directly from item.allocations[] -- never from the pooled
  // quantity/convertedQuantity/remainingQuantity mirror fields, which are only refreshed by
  // an actual allocation movement and can be stale/null immediately after PR creation.
  const totalPRQty = multiStoreHO
    ? items.reduce((sum, i) => sum + sumAllocations(i, "requestedQuantity"), 0)
    : null;
  const totalReservedQty = multiStoreHO
    ? items.reduce((sum, i) => sum + sumAllocations(i, "reservedQuantity"), 0)
    : null;
  const totalPOCreatedQty = multiStoreHO
    ? items.reduce((sum, i) => sum + sumAllocations(i, "convertedQuantity"), 0)
    : null;
  const totalShortClosedQty = multiStoreHO
    ? items.reduce((sum, i) => sum + sumAllocations(i, "shortClosedQuantity"), 0)
    : null;
  const totalBalanceQty = multiStoreHO
    ? items.reduce((sum, i) => sum + sumAllocationsAvailable(i), 0)
    : null;
  const allocation = multiStoreHO ? buildAllocationRows(pr) : null;

  const hasLinkedPO = (pr.purchaseOrderIds?.length || 0) > 0;
  // Editable pre-submission (created/reconsider/draft) as well as while a cycle is live,
  // per the reapproval-lifecycle feature -- but not once the PR is fully approved (that's
  // when Convert to PO takes over), and only up to the point a PO has actually been raised
  // against it, mirroring the backend's own gate.
  const canEdit =
    ["created", "reconsider", "draft", "Draft", "approval_in_progress"].includes(pr.status) &&
    !hasLinkedPO;
  // Statuses whose Edit click needs the "this will restart approval" confirmation first --
  // created/reconsider/draft PRs have no live cycle to interrupt, so they skip straight to the
  // edit form, unchanged from today.
  const editNeedsConfirm = pr.status === "approval_in_progress";
  // A parent PR (see state-split.service.js) is never directly submittable -- its children
  // are submitted individually/in bulk via the "Linked State PRs" section below instead.
  const canSubmit = ["created", "draft", "Draft"].includes(pr.status) && !pr.isParentPR;
  const canSubmitChildren =
    pr.isParentPR && childPRs.some((c) => CHILD_SUBMITTABLE_STATUSES.includes(c.status));
  const canCancel = pr.status === "created";
  const canProcurementActOnStorePR =
    isProcurementUser() &&
    pr.department == null &&
    pr.status === "approved" &&
    !(pr.purchaseOrderIds && pr.purchaseOrderIds.length > 0);
  const canConvertToPO = pr.status === "approved";
  // Store/State/Region-scoped multi-store HO PRs only -- Self-scoped and single-store PRs
  // have no store->vendor grouping to do (see the bulk-po-creation skill's "Scope" section)
  // and stay on the regular Convert to PO flow above. multiStoreHO already requires
  // prScope === "HEAD_OFFICE" and more than one resolved store, which SELF-scoped PRs
  // (no store dimension at all) can never satisfy.
  const canBulkConvertToPO = multiStoreHO && pr.status === "approved";
  // Procurement Budget / Procurement Budget Opex PRs can only be converted to a PO by
  // Procurement & Supply Chain, regardless of who raised them (see canConvertPRToPO) --
  // surfaced here as a heads-up for every other viewer, independent of whether Convert to
  // PO happens to be showing at all (e.g. it's still hidden pre-approval).
  const showRestrictedCategoryNotice = prHasRestrictedCategory(pr) && !isProcurementUser();
  const showApprovalActions =
    ["approval_in_progress", "reconsider"].includes(pr.status) && !!approvalDetails.id;
  // Approved-PR reconsideration (po-pr-short skill item #4) is a separate, narrower block from
  // showApprovalActions above -- deliberately not just adding "approved" to that array, since
  // that would also resurface Approve/Reject for an already-approved PR. Only Reconsider makes
  // sense here, and only for the one role the backend already allows to act on a fully-approved
  // item (see approval-service/index.js's reconsiderApproval role gate); the backend's own
  // linked-open-PO check (short-close.service.js/approval.controller.js) is the actual safety
  // net if this ever fires when it shouldn't.
  // Case-normalized, matching the backend's mirrored gates in approval-service/index.js's
  // reconsiderApproval and approval.controller.js's reconsiderApprovalById.
  const canReconsiderApprovedPR =
    pr.status === "approved" &&
    !!approvalDetails.id &&
    RECONSIDER_APPROVED_PR_ROLES.includes(
      String(getOfflineData("user")?.role || "").trim().toUpperCase()
    );
  // approvalDetails.id only reflects a live (non-archived) cycle, and previousApprovalIds only
  // covers PRs that were edited/resent -- neither is set once a single-store PR's approval is
  // fully done, so a completed PR would otherwise lose its history panel. Once approved, always
  // show it (PRApprovalCycles fetches the full cycle history itself, for either scope).
  // A parent PR (isParentPR) never itself goes through approval -- only its children do (see
  // state-split.service.js) -- so it never has stages/approver/pending-with info of its own to
  // show here, regardless of what its rolled-up status or previousApprovalIds happen to be.
  const showApprovalDetails =
    !pr.isParentPR &&
    (!!approvalDetails.id || pr.previousApprovalIds?.length > 0 || pr.status === "approved");

  const goToEditForm = () =>
    navigate(`/finance-v2/purchase-requisition/edit/${id}`);
  const onEditClick = () => {
    if (editNeedsConfirm) {
      setEditConfirmOpen(true);
    } else {
      goToEditForm();
    }
  };

  return (
    <div className="finance-v2-po-details-container">
      <div className="po-details-header">
        <div className="back-title">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography className="title">
            PR Details for {pr.prNumber || pr.id}
          </Typography>
        </div>
        <div className="right-section">
          <Button variant="outlined" onClick={printPR}>
            Print PR
          </Button>
          <Button variant="outlined" onClick={downloadPR}>
            Download PR
          </Button>

          {/* Draft actions */}
          {canEdit && isCreateAllowed("pr") && (
            <Button
              variant="outlined"
              disabled={isActionLoading}
              onClick={onEditClick}
            >
              Edit
            </Button>
          )}
          {canSubmit && isCreateAllowed("pr") && (
            <Button variant="outlined" disabled={isActionLoading} onClick={submitPR}>
              Submit for Approval
            </Button>
          )}
          {canSubmitChildren && isCreateAllowed("pr") && (
            <Button
              variant="outlined"
              disabled={submittingAllChildren}
              onClick={submitAllChildren}
            >
              Submit All State PRs
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outlined"
              color="error"
              disabled={isActionLoading}
              onClick={cancelPR}
            >
              Cancel PR
            </Button>
          )}

          {/* Procurement & Supply Chain acting on an approved Store PR -- see
              canProcurementActOnStorePR above. */}
          {canProcurementActOnStorePR && (
            <>
              <Button
                variant="outlined"
                disabled={isActionLoading}
                onClick={() => setReconsiderStorePRConfirmOpen(true)}
              >
                Reconsider Store PR
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={isActionLoading}
                onClick={() => setRejectStorePRConfirmOpen(true)}
              >
                Reject Store PR
              </Button>
            </>
          )}

          {/* Approval actions — shown for approval_in_progress and reconsider */}
          {showApprovalActions && (
            <>
              <Button
                variant="outlined"
                disabled={!checkIsApprover(approvalDetails)}
                onClick={() =>
                  setApproveRejectionDetails({ ...approvalDetails, toApprove: true })
                }
              >
                Approve
              </Button>
              <ReconsiderAction
                approvalDetails={approvalDetails}
                title="Reconsider PR"
                submitLabel="Reconsider PR"
                disabled={!checkIsApprover(approvalDetails)}
                onSuccess={() => {
                  loadPR();
                  loadApprovalDetails();
                }}
              />
              <Button
                variant="outlined"
                color="error"
                disabled={!checkIsApprover(approvalDetails)}
                onClick={() => setApproveRejectionDetails(approvalDetails)}
              >
                Reject
              </Button>
            </>
          )}

          {/* Approved-PR reconsideration -- see canReconsiderApprovedPR above; deliberately
              Reconsider only, not Approve/Reject. */}
          {canReconsiderApprovedPR && (
            <ReconsiderAction
              approvalDetails={approvalDetails}
              title="Reconsider PR"
              submitLabel="Reconsider PR"
              disabled={isActionLoading}
              onSuccess={() => {
                loadPR();
                loadApprovalDetails();
              }}
            />
          )}

          {/* Approved action -- gated on Create PO rights (not PR rights), so a user who can't
              create a PO can't convert a PR into one either, AND on canConvertPRToPO:
              Procurement & Supply Chain can convert any PR, a regular department "user" only
              their own department's PRs, and Store/other roles none at all. Backend enforces
              the same restriction independently (see convertToPO in purchase-request.controller.js). */}
          {canConvertToPO && isCreateAllowed("po") && canConvertPRToPO(pr) && (
            <Button
              variant="outlined"
              color="success"
              disabled={isActionLoading}
              onClick={convertToPO}
            >
              Convert to PO
            </Button>
          )}

          {/* Bulk PO -- same Create PO rights/canConvertPRToPO gate as Convert to PO above,
              since this is also a PO-creation action (backend gates it on the same po.create
              right). Shown alongside, not instead of, Convert to PO. */}
          {canBulkConvertToPO && isCreateAllowed("po") && canConvertPRToPO(pr) && (
            <Button
              variant="outlined"
              color="success"
              disabled={isActionLoading}
              onClick={() => setBulkPOModalOpen(true)}
            >
              Bulk PO
            </Button>
          )}

          {/* Standalone whole-PR short close (po-pr-short skill item #1b) -- see
              shortClosePRWhole above. pr.canShortCloseWholePR already carries the
              creator-only/Approved-or-Converted/no-open-linked-PO gating computed server-side
              (short-close.service.js's canShortCloseWholePR), independent of PO short close. */}
          {pr.canShortCloseWholePR && isCreateAllowed("pr") && (
            <Button
              variant="outlined"
              disabled={isActionLoading}
              onClick={() => setShortClosePRConfirmOpen(true)}
            >
              Short Close PR
            </Button>
          )}
        </div>
      </div>

      <div
        className={
          showApprovalDetails
            ? "po-detail-approval-stage"
            : "po-detail-approval-stage no-approval"
        }
      >
        <div className="po-details">
          <div className="form-container">
            <div className="info-section">
              <div className="info-box">
                <span>PR Number</span>
                <span>{pr.prNumber}</span>
              </div>
              <div className="info-box">
                <span>PR Date</span>
                <span>{formatDate(pr.prDate)}</span>
              </div>
              <div className="info-box">
                <span>Status</span>
                <span>
                  <Chip label={chipProps.label} sx={chipProps.sx} size="small" />
                </span>
              </div>
              <div className="info-box">
                <span>PR Type</span>
                <span>{pr.prType || "-"}</span>
              </div>
              <div className="info-box">
                <span>PR Scope</span>
                <span>
                  {pr.prScope === "HEAD_OFFICE" ? "Head Office" : "Single Store"}
                </span>
              </div>
              {pr.prScope === "HEAD_OFFICE" &&
                pr.storeSelectionType === "CITY" &&
                pr.selectedCities?.length > 0 && (
                  <div className="info-box">
                    <span>Target Cities</span>
                    <span>
                      {pr.selectedCities.map((city) => (
                        <Chip
                          key={city}
                          label={city}
                          size="small"
                          style={{ marginRight: 4, marginBottom: 4 }}
                        />
                      ))}
                    </span>
                  </div>
                )}
              {pr.prScope === "HEAD_OFFICE" &&
                pr.storeSelectionType === "REGION" &&
                pr.selectedRegions?.length > 0 && (
                  <div className="info-box">
                    <span>Target Regions</span>
                    <span>
                      {pr.selectedRegions.map((region) => (
                        <Chip
                          key={region}
                          label={region}
                          size="small"
                          style={{ marginRight: 4, marginBottom: 4 }}
                        />
                      ))}
                    </span>
                  </div>
                )}
              {pr.prScope === "HEAD_OFFICE" &&
                pr.storeSelectionType === "STATE" &&
                pr.selectedStates?.length > 0 && (
                  <div className="info-box">
                    <span>Target State</span>
                    <span>
                      {pr.selectedStates.map((state) => (
                        <Chip
                          key={state}
                          label={state}
                          size="small"
                          style={{ marginRight: 4, marginBottom: 4 }}
                        />
                      ))}
                    </span>
                  </div>
                )}
              {pr.prScope === "HEAD_OFFICE" &&
                ["SPECIFIC_STORES", "CITY", "STATE", "REGION"].includes(pr.storeSelectionType) &&
                pr.selectedSiteDetails?.length > 0 && (
                  <div className="info-box">
                    <span>Target Stores</span>
                    <span>
                      {pr.selectedSiteDetails.length <= INLINE_STORE_CHIP_THRESHOLD ? (
                        pr.selectedSiteDetails.map((s) => (
                          <Chip
                            key={s.id}
                            label={s.code ? `${s.code} - ${s.name}` : s.name}
                            size="small"
                            style={{ marginRight: 4, marginBottom: 4 }}
                          />
                        ))
                      ) : (
                        <Chip
                          label={`${pr.selectedSiteDetails.length} stores`}
                          size="small"
                          onClick={(e) => setStoresViewerAnchor(e.currentTarget)}
                        />
                      )}
                    </span>
                  </div>
                )}
              <div className="info-box">
                <span>Financial Year</span>
                <span>{pr.financialYear || "-"}</span>
              </div>
              <div className="info-box">
                <span>{pr.prScope === "HEAD_OFFICE" ? "HO Location" : "Store"}</span>
                <span>
                  {pr.siteCode
                    ? `${pr.siteCode} - ${pr.siteName}`
                    : pr.siteName || "-"}
                </span>
              </div>
              <div className="info-box">
                <span>Department</span>
                <span>{pr.departmentName || "Store"}</span>
              </div>
              <div className="info-box">
                <span>GST State</span>
                <span>{pr.gstState || "-"}</span>
              </div>
              <div className="info-box">
                <span>GST Number</span>
                <span>{pr.gstNumber || "-"}</span>
              </div>
              <div className="info-box">
                <span>Created By</span>
                <span>{pr.creatorName || "-"}</span>
              </div>
              <div className="info-box">
                <span>Base Amt</span>
                <span>{formatCurrency(pr.totalEstimatedAmount)}</span>
              </div>
              <div className="info-box">
                <span>GST Amt</span>
                <span>{formatCurrency(pr.totalEstimatedTaxAmount)}</span>
              </div>
              <div className="info-box">
                <span>Total Amt</span>
                <span>{formatCurrency(pr.totalEstimatedGrossAmount)}</span>
              </div>
              {pr.vendorRemarks && (
                <div className="info-box">
                  <span>Vendor Remarks</span>
                  <span>{pr.vendorRemarks}</span>
                </div>
              )}
              {pr.justification && (
                <div className="info-box">
                  <span>Justification</span>
                  <span>{pr.justification}</span>
                </div>
              )}
              <EditedPRChanges pr={pr} />
              {pr.documentLinks && pr.documentLinks.length > 0 && (
                <div className="info-box">
                  <span>Documents</span>
                  <span>
                    {pr.documentLinks.map((url, idx) => (
                      <Chip
                        key={idx}
                        label={`Document ${idx + 1}`}
                        size="small"
                        onClick={() => window.open(url, "_blank")}
                        style={{ marginRight: 4, cursor: "pointer" }}
                      />
                    ))}
                  </span>
                </div>
              )}
              {pr.purchaseOrderId && (
                <div className="info-box">
                  <span>Linked PO</span>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      onClick={() =>
                        navigate(`purchase-order/${pr.purchaseOrderId}`)
                      }
                    >
                      View PO
                    </Button>
                  </span>
                </div>
              )}
            </div>
          </div>

          {showRestrictedCategoryNotice && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              Only Procurement & Supply Chain can convert this PR into a PO
            </Alert>
          )}

          {pr.isParentPR && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              {childPRsLoading
                ? "Loading linked state PRs..."
                : canSubmitChildren
                ? 'This PR was split by state into the requisitions listed below under ' +
                  '"Linked State PRs". This coordination record itself is never sent for ' +
                  'approval -- submit each state PR individually, or use "Submit All State ' +
                  'PRs" above.'
                : "This PR is a parent PR, scroll down to see the Linked State PRs below."}
            </Alert>
          )}

          <div className="form-container">
            <MuiTable
              columnsList={getPRItemsConfig(headOfficePR, pr, openShortCloseModal)}
              dataList={items.slice(
                ((filters.page || 1) - 1) * 10,
                ((filters.page || 1) - 1) * 10 + 10
              )}
              pageCount={Math.ceil(items.length / 10)}
              onChange={(page) => setFilters({ ...filters, page })}
            />
          </div>

          {multiStoreHO && allocation.rows.length > 0 && (
            <div className="form-container">
              <Typography className="section-title">Store Allocation Breakdown</Typography>
              <div className="pr-multi-store-summary">
                <div className="stat-chip">
                  <span className="stat-label">Store Count</span>
                  <span className="stat-value">{storeCount}</span>
                </div>
                <div className="stat-chip">
                  <span className="stat-label">Total PR Qty</span>
                  <span className="stat-value">{totalPRQty}</span>
                </div>
                <div className="stat-chip">
                  <span className="stat-label">Total Reserved Qty</span>
                  <span className="stat-value">{totalReservedQty}</span>
                </div>
                <div className="stat-chip">
                  <span className="stat-label">Total PO Created Qty</span>
                  <span className="stat-value">{totalPOCreatedQty}</span>
                </div>
                <div className="stat-chip">
                  <span className="stat-label">Total Short-Closed Qty</span>
                  <span className="stat-value">{totalShortClosedQty}</span>
                </div>
                <div className="stat-chip">
                  <span className="stat-label">Total Balance Qty</span>
                  <span className="stat-value">{totalBalanceQty}</span>
                </div>
              </div>
              <MuiTable
                columnsList={getStoreAllocationColumns(navigate)}
                dataList={allocation.rows.slice(
                  ((allocationFilters.page || 1) - 1) * 10,
                  ((allocationFilters.page || 1) - 1) * 10 + 10
                )}
                pageCount={Math.ceil(allocation.rows.length / 10)}
                onChange={(page) => setAllocationFilters({ ...allocationFilters, page })}
              />
            </div>
          )}

          {pr.purchaseOrderIds?.length > 0 && (
            <div className="form-container">
              <Typography className="section-title">Purchase Orders from this PR</Typography>
              <MuiTable
                columnsList={getRelatedPOColumns(navigate, multiStoreHO, pr.selectedSiteDetails || [])}
                dataList={pr.purchaseOrderIds}
              />
            </div>
          )}

          {pr.isParentPR && (
            <div className="form-container">
              <Typography className="section-title">Linked State PRs</Typography>
              {childPRsLoading ? (
                <Typography variant="body2">Loading linked state PRs...</Typography>
              ) : (
                <MuiTable
                  columnsList={getChildPRColumns(navigate, submitOneChild, submittingChildId)}
                  dataList={childPRs}
                />
              )}
            </div>
          )}
        </div>

        {showApprovalDetails && (
          <div className="approval-details">
            <PRApprovalCycles
              prId={pr.id || pr._id}
              createdBy={pr.creatorName}
              fallbackCurrent={approvalDetails}
            />
          </div>
        )}
      </div>

      {approveRejectDetails && (
        <CustomModal
          title={`${approveRejectDetails.toApprove ? "Approve PR" : "Reject PR"}`}
          disabledOutsideClick={true}
          onClose={() => setApproveRejectionDetails(null)}
        >
          <div className="finance-v2-b-modal-form-container">
            <TextField
              value={approveRejectDetails.comment || ""}
              onChange={(e) =>
                setApproveRejectionDetails({
                  ...approveRejectDetails,
                  comment: e.target.value,
                })
              }
              size="small"
              name="comment"
              label="Comment"
              fullWidth
            />
            <Button
              color={approveRejectDetails.toApprove ? "primary" : "error"}
              variant="outlined"
              onClick={() => {
                if (approveRejectDetails.toApprove) {
                  onApprove();
                  return;
                }
                onReject();
              }}
              fullWidth
              disabled={
                approveRejectDetails.toApprove
                  ? false
                  : !approveRejectDetails.comment
              }
            >
              {approveRejectDetails.toApprove ? "Approve" : "Reject"}
            </Button>
          </div>
        </CustomModal>
      )}

      {sentPrDetails && (
        <CustomModal title="PR Submitted for Approval" disabledOutsideClick={true}>
          <ApprovalInfo
            poDetails={{
              poNumber: pr.prNumber,
              items: items.map((i) => ({
                quantity: i.quantity,
                taxRate: i.taxRate,
                rate: i.estimatedRate,
              })),
            }}
            siteCode={pr.siteCode || ""}
            siteName={pr.siteName || ""}
            pendingWith={(sentPrDetails && sentPrDetails.pendingWith) || ""}
            onOk={() => navigate(-1)}
          />
        </CustomModal>
      )}

      {bulkPOModalOpen && (
        <BulkPOModal
          prId={pr.id || pr._id}
          onClose={() => setBulkPOModalOpen(false)}
          onGenerated={loadPR}
        />
      )}

      {shortCloseItem && (
        <CustomModal
          title={`Short Close - ${getItemIdentity(shortCloseItem).name || getItemIdentity(shortCloseItem).code || "Item"}`}
          disabledOutsideClick={true}
          onClose={() => setShortCloseItem(null)}
        >
          <div className="finance-v2-b-modal-form-container">
            <Typography variant="body2" color="text.secondary">
              Closes this line item's entire remaining balance below and releases its
              reserved budget for redeployment. This cannot be undone.
            </Typography>
            {shortCloseOpenAllocations.map((a) => {
              const storeId = normalizeId(a.storeId);
              const available = computeAvailable(a);
              return (
                <div
                  key={storeId}
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography variant="body2">{getStoreLabel(pr, storeId)}</Typography>
                  <Typography variant="body2">{available}</Typography>
                </div>
              );
            })}
            <TextField
              value={shortCloseRemarks}
              onChange={(e) => setShortCloseRemarks(e.target.value)}
              size="small"
              name="remarks"
              label="Remarks (optional)"
              fullWidth
              multiline
            />
            <Button
              variant="outlined"
              color="error"
              fullWidth
              disabled={isActionLoading}
              onClick={submitShortClose}
            >
              Confirm Short Close
            </Button>
          </div>
        </CustomModal>
      )}

      <EditPRConfirmDialog
        open={editConfirmOpen}
        prStatus={pr.status}
        onClose={() => setEditConfirmOpen(false)}
        onConfirm={() => {
          setEditConfirmOpen(false);
          goToEditForm();
        }}
      />

      {shortClosePRConfirmOpen && (
        <CustomModal
          title="Short Close Purchase Requisition"
          disabledOutsideClick={true}
          onClose={() => setShortClosePRConfirmOpen(false)}
        >
          <div className="finance-v2-b-modal-form-container">
            <Typography>
              Are you sure you want to short close this Purchase Requisition?
              This cannot be undone.
            </Typography>
            <Button
              variant="outlined"
              color="warning"
              fullWidth
              disabled={isActionLoading}
              onClick={shortClosePRWhole}
            >
              Short Close PR
            </Button>
          </div>
        </CustomModal>
      )}

      {reconsiderStorePRConfirmOpen && (
        <CustomModal
          title="Reconsider Store PR"
          disabledOutsideClick={true}
          onClose={() => setReconsiderStorePRConfirmOpen(false)}
        >
          <div className="finance-v2-b-modal-form-container">
            <Typography>
              Are you sure you want to reconsider this Store PR? It will be
              sent back for the creator to edit and resubmit.
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              disabled={isActionLoading}
              onClick={reconsiderStorePR}
            >
              Reconsider Store PR
            </Button>
          </div>
        </CustomModal>
      )}

      {rejectStorePRConfirmOpen && (
        <CustomModal
          title="Reject Store PR"
          disabledOutsideClick={true}
          onClose={() => setRejectStorePRConfirmOpen(false)}
        >
          <div className="finance-v2-b-modal-form-container">
            <Typography>
              Are you sure you want to reject this Store PR? This cannot be
              undone.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              disabled={isActionLoading}
              onClick={rejectStorePR}
            >
              Reject Store PR
            </Button>
          </div>
        </CustomModal>
      )}

      <ResolvedStoresViewer
        anchorEl={storesViewerAnchor}
        open={Boolean(storesViewerAnchor)}
        onClose={() => setStoresViewerAnchor(null)}
        title="Target Stores"
        stores={pr.selectedSiteDetails || []}
      />
    </div>
  );
};

export default PRDetails;
